import React, { ChangeEvent, MouseEventHandler, useState } from "react";
import styled from "styled-components";
import {
  Button,
  ButtonProps,
  CheckboxDefault,
  TextInput,
  Theme,
} from "@literati/public-ui-shared-react";

import { FormikChildrenRenderProps } from "components/Step/StepLayout";

import AuthFormTextInput from "components/authenticate/AuthFormTextInput";
import AuthFormToggle from "components/authenticate/AuthFormToggle";
import { AuthKey, AuthValues } from "components/authenticate/authTypes";
import SocialButtonsContainer from "components/authenticate/SocialButtonsContainer";

import { CheckoutDummyAccountLegalCheckbox } from "components/checkout/CheckoutDummyAccountLegalCheckbox";

const CLASS_BASE = "auth-form-section-create-account";
export const AuthFormSectionCreateAccountClassName = {
  TERMS_CHECKBOX: `${CLASS_BASE}-terms-checkbox`,
  MAILING_LIST_CHECKBOX: `${CLASS_BASE}-mailing-list-checkbox`,
} as const;

const StyledCheckbox = styled(CheckboxDefault)`
  margin: 1rem 0 0;
  font-size: 0.75rem;
`;

const StyledButton = styled(Button)`
  margin: 1.5rem 0 0;
  width: 100%;
  max-width: none;
  min-height: unset;
  height: 40px;
  font-weight: 700;
`;

export interface AuthFormSectionCreateAccountProps
  extends FormikChildrenRenderProps<AuthValues> {
  formId?: string;
  onFormToggleClick?: MouseEventHandler<HTMLButtonElement>;
  onPhoneNumberChange?: (phoneNumber: string) => void;
  onSocialLoginSuccess?: () => void;
  showSocialLogin?: boolean;
  submitButtonProps?: ButtonProps;
  withTerms?: boolean;
}

export const AuthFormSectionCreateAccount = ({
  errors,
  formId,
  onFormToggleClick,
  onPhoneNumberChange,
  onSocialLoginSuccess,
  showSocialLogin = false,
  submitButtonProps,
  submitCount,
  touched,
  withTerms = false,
}: AuthFormSectionCreateAccountProps) => {
  const cls = AuthFormSectionCreateAccountClassName;

  const [loginBlurred, setLoginBlurred] = useState(false);
  const [passwordBlurred, setPasswordBlurred] = useState(false);

  const [phoneNumber, setPhoneNumber] = useState("");

  const handlePhoneNumberChange = (event: ChangeEvent<HTMLInputElement>) => {
    const rawValue = event?.target?.value || "";

    // keep only digits and hyphens for display
    const formattedValue = rawValue.replace(/[^0-9-]/g, "");
    setPhoneNumber(formattedValue);

    // only digits for callback/submission
    const unformattedValue = rawValue.replace(/[^\d]/g, "");
    onPhoneNumberChange?.(unformattedValue);
  };

  return (
    <Theme>
      <AuthFormTextInput
        // api errors come back keyed as email
        errors={{ login: errors.login || errors.email }}
        hasBlurred={loginBlurred}
        onBlur={() => setLoginBlurred(true)}
        submitCount={submitCount}
        variant="login"
      />
      <TextInput
        css="margin: 1rem 0 0"
        label="Phone Number (optional)"
        name="phone"
        onChange={handlePhoneNumberChange}
        type="tel"
        value={phoneNumber}
        maxLength={15}
      />
      <AuthFormTextInput
        css="margin: 1rem 0 0"
        errors={errors}
        hasBlurred={passwordBlurred}
        onBlur={() => setPasswordBlurred(true)}
        submitCount={submitCount}
        variant="password"
      />
      {withTerms ? (
        <>
          <CheckoutDummyAccountLegalCheckbox
            className={cls.TERMS_CHECKBOX}
            css="margin: 1.5rem 0 0"
            hasFieldError={Boolean(errors?.[AuthKey.TERMS])}
            touched={touched?.[AuthKey.TERMS]}
          />
          <StyledCheckbox
            className={cls.MAILING_LIST_CHECKBOX}
            label="Yes, I want to get email updates about Literati’s new products, promotions, and bookish fun."
            name="mailingList"
          />
        </>
      ) : null}
      {showSocialLogin ? (
        <div
          css={`
            display: flex;
            flex-direction: column;
            align-items: center;
            max-width: 300px;
            margin: auto;
          `}
        >
          <div css="width: 100%">
            <StyledButton form={formId} type="submit" {...submitButtonProps}>
              Continue
            </StyledButton>
            <SocialButtonsContainer
              connectAccountUXType="dialog"
              css="margin: 1rem 0 0"
              disableAutoTap={true}
              onSuccess={onSocialLoginSuccess}
              width={384}
            />
          </div>
        </div>
      ) : null}
      <AuthFormToggle onClick={onFormToggleClick} variant="createAccount" />
    </Theme>
  );
};

export default AuthFormSectionCreateAccount;
